import { useState } from "react";
import type { InputHTMLAttributes } from "react";
import { Icon, type IconName } from "../icon/Icon";

type Size = "large" | "medium";

const SIZES: Record<Size, { height: number; font: string; lh: string; pad: number }> = {
  large: { height: 48, font: "var(--font-size-body-1)", lh: "var(--line-height-body-1)", pad: 16 },
  medium: { height: 40, font: "var(--font-size-body-2)", lh: "var(--line-height-body-2)", pad: 14 },
};

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  size?: Size;
  iconLeft?: IconName;
  iconRight?: IconName;
}

export function TextField({
  label,
  value,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  size = "large",
  iconLeft,
  iconRight,
  type = "text",
  style,
  ...rest
}: TextFieldProps) {
  const s = SIZES[size] || SIZES.large;
  const [focused, setFocused] = useState(false);
  const borderColor = error
    ? "var(--status-negative)"
    : focused
      ? "var(--primary-normal)"
      : "var(--line-normal-normal)";
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "var(--font-sans)", ...style }}>
      {label && (
        <span
          style={{
            fontSize: "var(--font-size-label-1)",
            lineHeight: "var(--line-height-label-1)",
            fontWeight: 600,
            color: "var(--label-normal)",
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: s.height,
          padding: `0 ${s.pad}px`,
          borderRadius: "var(--radius-md)",
          background: disabled ? "var(--background-normal-alternative)" : "var(--background-normal-normal)",
          boxShadow: `inset 0 0 0 ${focused && !error ? 1.5 : 1}px ${borderColor}`,
          transition: "box-shadow var(--duration-fast) var(--ease-standard)",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {iconLeft && (
          <Icon name={iconLeft} size={20} style={{ color: "var(--label-assistive)", flex: "none" }} />
        )}
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            // input 기본 intrinsic 너비(size=20, 약 170px)는 min-width:0 으로도 부모의
            // min-content 계산에서 빠지지 않아 좁은 화면에서 flex 행을 밖으로 밀어낸다.
            // width:0 으로 기여분을 지우고 flex-grow 로만 채운다.
            width: 0,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "var(--font-sans)",
            fontSize: s.font,
            lineHeight: s.lh,
            color: "var(--label-normal)",
          }}
          {...rest}
        />
        {iconRight && (
          <Icon name={iconRight} size={20} style={{ color: "var(--label-assistive)", flex: "none" }} />
        )}
      </div>
      {helperText && (
        <span
          style={{
            fontSize: "var(--font-size-label-2)",
            lineHeight: "var(--line-height-label-2)",
            color: error ? "var(--status-negative)" : "var(--label-alternative)",
          }}
        >
          {helperText}
        </span>
      )}
    </label>
  );
}

export default TextField;
